/**
 * @file interpret/components/useCallMicDefault.ts
 * @brief While Call mode sends to Verblizr Microphone, it is the Mac's
 *        microphone, so Zoom / Teams / Meet use it with no setting (DES
 *        03-10-2026: "like plugging in headphones").
 *
 * @details
 * The desktop app's helper (DESKTOP/src/callMic.js) switches the Mac's default
 * input and puts the previous one back when this turns off: leaving the
 * Interpreter, turning Call mode off, quitting. Our own capture must then
 * never follow "System default", or it would hear our own translation: the
 * helpers in callMicDevices.ts hide Verblizr Microphone from the mic list and resolve
 * "System default" to the mic that was the default before.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useEffect, useState } from "react";
import { desktopShell } from "@/shared/desktop/desktopShell";

/** Makes Verblizr Microphone the Mac's mic while `active`; returns the mic it replaced. */
export function useCallMicDefault(active: boolean): string | null {
  const [previous, setPrevious] = useState<string | null>(null);
  useEffect(() => {
    const shell = desktopShell();
    if (!active || !shell?.setCallMicDefault) return;
    let cancelled = false;
    void shell
      .setCallMicDefault(true)
      .then((r) => {
        if (!cancelled && r.ok) setPrevious(r.previous || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      setPrevious(null);
      void shell.setCallMicDefault?.(false).catch(() => undefined);
    };
  }, [active]);
  return previous;
}
